import React from 'react';

import visa from '../../../../assets/pages/payment/visa.png';
import mastercard from '../../../../assets/pages/payment/mastercard.png';
import amex from '../../../../assets/pages/payment/amex.png';
import {
  PaymentMethodType,
  TenantPaymentOptionStatusEnum,
  getPadVerificationLabel,
  getPaymentOptionStatusLabel,
} from '../../../../helpers/utils/types';
import { capitalizeFirstLetter } from '../../../../helpers/utils/utils';

export enum FeeDisplayType {
  Free = 'free',
  Fixed = 'fixed',
  Percentage = 'percentage',
}

export type FeeDisplayValue = 'Free' | `$${string}` | `${string}%`;

export interface FeeDisplayResult {
  display: FeeDisplayValue;
  type: FeeDisplayType;
}

interface CardDesignProps {
  cardBrand?: string;
  cardEndingDigits?: string;
  cardExpiryDate?: string;
  isActive?: boolean;
  paymentMethod?: string;
  isDefault?: boolean;
  editMode?: boolean;
  percentageRate: string;
  cardCategoryName: string;
  status?: string;
  bankAccountName?: string | null;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  isBankAccountLinked?: boolean;
  fixedProcessingFee?: string;
  fees?: Array<{
    tenantProcessingFeeId: string;
    percentageRate: string;
    fixedProcessingFee: string;
    paymentType: string;
  }>;
  paymentType?: string;
}

// ─── SVG primitives ───────────────────────────────────────────────────────────

const ChipIcon = () => (
  <svg width='32' height='24' viewBox='0 0 32 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect width='32' height='24' rx='4' fill='#C9A040' />
    <rect x='11' y='0' width='10' height='24' rx='1' fill='#8B6C1A' opacity='0.45' />
    <rect x='0' y='9' width='32' height='6' fill='#8B6C1A' opacity='0.45' />
    <rect x='11' y='9' width='10' height='6' fill='#A07820' opacity='0.55' />
  </svg>
);

const BankIcon = ({ light }: { light: boolean }) => (
  <div
    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
      light ? 'bg-[#0055FF]/10' : 'bg-white/10'
    }`}
  >
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M3 22V9L12 2L21 9V22H3Z'
        stroke={light ? '#0055FF' : '#FFFFFF'}
        strokeWidth='1.6'
        strokeLinejoin='round'
      />
      <path
        d='M9 22V16H15V22'
        stroke={light ? '#0055FF' : '#FFFFFF'}
        strokeWidth='1.6'
        strokeLinejoin='round'
      />
      <path d='M3 9H21' stroke={light ? '#0055FF' : '#FFFFFF'} strokeWidth='1.6' />
    </svg>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CardDesign: React.FC<CardDesignProps> = ({
  cardBrand,
  cardEndingDigits,
  isActive,
  paymentMethod,
  isDefault,
  percentageRate,
  cardCategoryName,
  status,
  bankName,
  bankAccountNumber,
  isBankAccountLinked,
  fixedProcessingFee,
  fees,
  paymentType,
}) => {
  const isPreAuthorizedDebit = paymentMethod === PaymentMethodType.PreAuthorizedDebit;

  const getFeeForPaymentType = () => {
    if (!fees || !paymentType) return null;
    return fees.find((f) => f.paymentType === paymentType) ?? null;
  };

  const formatFeeDisplay = (): FeeDisplayResult | null => {
    const fee = getFeeForPaymentType();
    if (!fee) return null;

    const fixedFee = parseFloat(fee.fixedProcessingFee || '0');
    const percentageRate = parseFloat(fee.percentageRate || '0');

    if (fixedFee === 0 && percentageRate === 0)
      return { display: 'Free', type: FeeDisplayType.Free };
    if (fixedFee > 0)
      return { display: `$${fixedFee.toFixed(2)}` as FeeDisplayValue, type: FeeDisplayType.Fixed };
    if (percentageRate > 0)
      return {
        display: `${fee.percentageRate}%` as FeeDisplayValue,
        type: FeeDisplayType.Percentage,
      };
    return null;
  };

  const feeDisplay = formatFeeDisplay();

  const feeValue = feeDisplay
    ? feeDisplay.display
    : fixedProcessingFee && parseFloat(fixedProcessingFee) > 0
      ? `$${fixedProcessingFee}`
      : `${percentageRate}%`;

  const getFeeSub = (): string => {
    if (feeDisplay) {
      if (feeDisplay.type === FeeDisplayType.Free) return 'No fee';
      if (feeDisplay.type === FeeDisplayType.Fixed) return 'Fixed fee';
      return 'Processing rate';
    }
    if (fixedProcessingFee && parseFloat(fixedProcessingFee) > 0) return 'Fixed fee';
    return 'Processing rate';
  };

  const feeSub = getFeeSub();

  const last4 = bankAccountNumber ? bankAccountNumber.slice(-4) : (cardEndingDigits ?? '····');

  const categoryLabel = cardCategoryName ? capitalizeFirstLetter(cardCategoryName) : null;

  // ── PAD — light card ──────────────────────────────────────────────────────
  if (isPreAuthorizedDebit) {
    return (
      <div
        className='group relative w-[min(300px,calc(100vw-88px))] xs:w-[300px] flex items-center justify-center'
        data-clarity-mask='true'
      >
        <div
          className={`relative w-full aspect-[268/155] rounded-[16px] sm:rounded-[20px] overflow-hidden transition-all duration-200
            bg-gradient-to-br from-[#F8F9FC] to-[#EDF0F8]
            ${isActive ? 'ring-2 ring-[#0055FF]' : 'ring-1 ring-[#E2E6F0]'}`}
        >
          {/* Dot grid */}
          <div
            className='absolute inset-0 opacity-[0.06]'
            style={{
              backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
              backgroundSize: '14px 14px',
            }}
          />

          <div className='relative flex flex-col h-full px-4 sm:px-6 pt-3.5 sm:pt-[18px] pb-3.5 sm:pb-[18px]'>
            {/* Top row */}
            <div className='flex items-start justify-between'>
              <div className='flex items-center gap-2.5 min-w-0'>
                <BankIcon light={true} />
                <div className='flex flex-col min-w-0'>
                  <span className='text-[13px] font-bold text-[#0A0B14] leading-tight truncate'>
                    Pre-authorized debit
                  </span>
                  {status && status !== TenantPaymentOptionStatusEnum.Active && (
                    <span className='mt-1 self-start px-2 py-0.5 rounded-full text-[9px] font-semibold bg-yellow-100 text-yellow-700'>
                      {getPaymentOptionStatusLabel(status)}
                    </span>
                  )}
                </div>
              </div>
              {!!isDefault && status === TenantPaymentOptionStatusEnum.Active && (
                <div className='flex items-center gap-1 px-2.5 py-[5px] rounded-full bg-[rgba(0,85,255,0.08)] border border-[rgba(0,85,255,0.14)] flex-shrink-0 ml-2'>
                  <svg width='5' height='5' viewBox='0 0 6 6'>
                    <circle cx='3' cy='3' r='3' fill='#0055FF' />
                  </svg>
                  <span className='text-[10px] font-semibold text-[#0055FF]'>Default</span>
                </div>
              )}
            </div>

            {/* Masked account number */}
            <div className='mt-4 flex-1 flex items-center'>
              <span className='text-[14px] tracking-[0.22em] font-medium text-[#3D4462]'>
                •••• {last4}
              </span>
              <span className='ml-2 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-[rgba(0,85,255,0.08)] text-[#0055FF]'>
                {getPadVerificationLabel(isBankAccountLinked)}
              </span>
            </div>

            {/* Fee row */}
            <div className='flex items-end justify-between mt-auto'>
              <div>
                <div className='text-[9px] font-semibold tracking-widest text-[#8B91A8] uppercase mb-[3px]'>
                  Fee
                </div>
                <div className='flex items-baseline gap-1.5'>
                  <span className='text-[12px] font-bold text-[#0A0B14] leading-none'>{feeValue}</span>
                  <span className='text-[9px] text-[#8B91A8] font-medium'>· {feeSub}</span>
                </div>
              </div>

              {bankName && (
                <div className='flex-shrink-0 max-w-[130px] text-right'>
                  <span className='text-[12px] font-bold text-[#0A0B14] leading-tight truncate block'>
                    {bankName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Credit / Debit — dark card ────────────────────────────────────────────
  return (
    <div
      className='group relative w-[min(300px,calc(100vw-88px))] xs:w-[300px] flex items-center justify-center'
      data-clarity-mask='true'
    >
      <div
        className={`relative w-full aspect-[268/155] rounded-[16px] sm:rounded-[20px] overflow-hidden transition-all duration-200
          bg-gradient-to-br from-[#0B1C42] via-[#0E2050] to-[#06102C]
          ${isActive ? 'ring-2 ring-[#2155EC]' : 'ring-1 ring-white/[0.08]'}`}
      >
        {/* Dot grid overlay */}
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        />
        {/* Radial highlight top-left */}
        <div className='absolute -top-12 -left-12 w-44 h-44 rounded-full blur-3xl pointer-events-none bg-[#2155EC] opacity-20' />

        <div className='relative flex flex-col h-full px-4 sm:px-6 pt-3.5 sm:pt-[18px] pb-3.5 sm:pb-[18px] text-white'>
          {/* Top row */}
          <div className='flex items-start justify-between'>
            <div className='flex items-center gap-2.5 min-w-0'>
              <div className='flex-shrink-0 mt-0.5'>
                <ChipIcon />
              </div>
              <div className='flex flex-col min-w-0'>
                <span className='text-[10px] font-semibold tracking-[0.18em] text-white/50 uppercase truncate'>
                  {paymentMethod ?? 'Card'}
                </span>
                <span className='text-[13px] font-bold leading-tight truncate'>
                  {categoryLabel ?? cardBrand ?? 'Card'}
                </span>
                {status && status !== TenantPaymentOptionStatusEnum.Active && (
                  <span className='mt-1 self-start px-2 py-[3px] rounded-full text-[9px] font-semibold bg-yellow-400/20 text-yellow-300'>
                    {getPaymentOptionStatusLabel(status)}
                  </span>
                )}
              </div>
            </div>

            <div className='flex items-start gap-1.5 flex-shrink-0 ml-2'>
              {!!isDefault && (
                <div className='flex items-center gap-1 px-2.5 py-[5px] rounded-full bg-white/10 border border-white/20'>
                  <svg width='5' height='5' viewBox='0 0 6 6'>
                    <circle cx='3' cy='3' r='3' fill='white' />
                  </svg>
                  <span className='text-[10px] font-semibold text-white'>Default</span>
                </div>
              )}
            </div>
          </div>

          {/* Card number */}
          <div className='mt-3 flex-1 flex items-center gap-2'>
            <span className='text-[13px] tracking-[0.20em] font-medium text-white/75'>
              •••• •••• •••• {last4}
            </span>
            {categoryLabel && (
              <span className='px-2 py-0.5 rounded-full text-[9px] font-semibold bg-white/10 text-white/70'>
                {categoryLabel}
              </span>
            )}
          </div>

          {/* Bottom row */}
          <div className='flex items-end justify-between mt-auto'>
            <div>
              <div className='text-[9px] font-semibold tracking-widest text-white/50 uppercase mb-[3px]'>
                Fee
              </div>
              <div className='flex items-baseline gap-1.5'>
                <span className='text-[12px] font-bold leading-none'>{feeValue}</span>
                <span className='text-[9px] text-white/50 font-medium'>· {feeSub}</span>
              </div>
            </div>

            {/* Brand logo */}
            <div className='flex-shrink-0'>
              {cardBrand === 'Visa' && (
                <img src={visa} alt='Visa' className='h-[18px] brightness-0 invert opacity-85' />
              )}
              {cardBrand === 'Mastercard' && (
                <img src={mastercard} alt='Mastercard' className='h-6 opacity-90' />
              )}
              {cardBrand === 'Amex' && (
                <img src={amex} alt='Amex' className='h-7 w-8 brightness-0 invert opacity-85' />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardDesign;
